import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";
// Clock-skew allowance when matching a stored result to the run that produced it.
const RESULT_SKEW_MS = 2 * 60 * 1000;

// API Gateway stops waiting after ~29s (503/504, sometimes without CORS headers,
// which the browser reports as a network TypeError) while the test keeps running.
export function isBackgroundRun({ status, error } = {}) {
  return status === 503 || status === 504 || error instanceof TypeError;
}

export function isFreshResult(data, startedAt) {
  if (!data || !(data.validation || data.summary)) return false;
  if (!startedAt) return true;
  const raw = data.timestamp;
  if (!raw) return false;
  const text = String(raw);
  const stamp = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
  return Number.isFinite(stamp) && stamp >= startedAt - RESULT_SKEW_MS;
}

function SecurityFieldTesting() {
  const [activeTab, setActiveTab] = useState("field-test");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [securityClasses, setSecurityClasses] = useState(null);
  const [testType, setTestType] = useState("full_matrix");
  const [selectedRole, setSelectedRole] = useState("GHR:H:BHRwithApprovals");
  const [selectedCPEH, setSelectedCPEH] = useState("CPXH");
  
  // Bulk test states
  const [bulkTestRunning, setBulkTestRunning] = useState(false);
  const [bulkTestResults, setBulkTestResults] = useState([]);
  const [bulkTestProgress, setBulkTestProgress] = useState({ completed: 0, total: 0 });
  const [bulkTestFilter, setBulkTestFilter] = useState("all"); // all, failed, passed

  // Multi-select states for single test tab
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState([]);
  const [selectedCombinations, setSelectedCombinations] = useState([]); // Array of {role, cpeh}
  const [multiTestResults, setMultiTestResults] = useState([]);
  const [multiTestRunning, setMultiTestRunning] = useState(false);
  const [multiTestProgress, setMultiTestProgress] = useState({ completed: 0, total: 0 });

  // Mapping of roles to their available CPEH variations (only those with credentials)
  const roleCPEHMapping = {
    "GHR:H:BHRwithApprovals": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:BHRwithoutApprovals": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:ComplianceViewOnly": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:HireActionProcessor": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:HROperationsAdmin": ["CPEH"],
    "GHR:H:HROperationsAdminBenefits": ["CPEH"],
    "GHR:H:HROperationsAdvanced": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:HROperationsBHRSupport": ["XXXX", "CPEX", "CPXX", "CPXH", "CPEH"],
    "GHR:H:HROperationsStandard": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:HRViewOnly": ["CXEH", "CXEX", "CXXH", "CXXX", "CPEH", "CPEX", "CPXH", "CPXX", "XPEH", "XPEX", "XPXH", "XPXX", "XXEH", "XXEX", "XXXH", "XXXX"],
    "GHR:H:OperationsAdvancedPayroll": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:OperationsStandardPayroll": ["CPXX", "CPXH", "CPEH"],
    "GHR:H:RecordsMaintenance": ["CPXH"],
  };

  // All CPEH variations with labels
  const cpehLabels = {
    "CPEH": "CPEH (Comp ✓, PII ✓, Exec ✓, HR ✓)",
    "CPEX": "CPEX (Comp ✓, PII ✓, Exec ✓, HR ✗)",
    "CPXH": "CPXH (Comp ✓, PII ✓, Exec ✗, HR ✓)",
    "CPXX": "CPXX (Comp ✓, PII ✓, Exec ✗, HR ✗)",
    "CXEH": "CXEH (Comp ✓, PII ✗, Exec ✓, HR ✓)",
    "CXEX": "CXEX (Comp ✓, PII ✗, Exec ✓, HR ✗)",
    "CXXH": "CXXH (Comp ✓, PII ✗, Exec ✗, HR ✓)",
    "CXXX": "CXXX (Comp ✓, PII ✗, Exec ✗, HR ✗)",
    "XPEH": "XPEH (Comp ✗, PII ✓, Exec ✓, HR ✓)",
    "XPEX": "XPEX (Comp ✗, PII ✓, Exec ✓, HR ✗)",
    "XPXH": "XPXH (Comp ✗, PII ✓, Exec ✗, HR ✓)",
    "XPXX": "XPXX (Comp ✗, PII ✓, Exec ✗, HR ✗)",
    "XXEH": "XXEH (Comp ✗, PII ✗, Exec ✓, HR ✓)",
    "XXEX": "XXEX (Comp ✗, PII ✗, Exec ✓, HR ✗)",
    "XXXH": "XXXH (Comp ✗, PII ✗, Exec ✗, HR ✓)",
    "XXXX": "XXXX (Comp ✗, PII ✗, Exec ✗, HR ✗)",
  };

  // Get available roles
  const availableRoles = Object.keys(roleCPEHMapping);

  // Generate all role-CPEH combinations
  const allCombinations = [];
  Object.entries(roleCPEHMapping).forEach(([role, cpehs]) => {
    cpehs.forEach(cpeh => {
      allCombinations.push({ role, cpeh });
    });
  });

  // Get available CPEH options for the selected role
  const getAvailableCPEH = (role) => {
    const cpehValues = roleCPEHMapping[role] || [];
    return cpehValues.map(value => ({
      value: value,
      label: cpehLabels[value]
    }));
  };

  // Handle role change - auto-select first available CPEH
  const handleRoleChange = (newRole) => {
    setSelectedRole(newRole);
    const availableCPEHForRole = roleCPEHMapping[newRole] || [];
    if (availableCPEHForRole.length > 0) {
      setSelectedCPEH(availableCPEHForRole[0]);
    }
  };

  // Toggle multi-select mode
  const toggleMultiSelectMode = () => {
    setMultiSelectMode(!multiSelectMode);
    if (multiSelectMode) {
      // Exiting multi-select mode
      setExpandedRoles([]);
      setSelectedCombinations([]);
    }
  };

  // Toggle role expansion
  const toggleRoleExpansion = (role) => {
    if (expandedRoles.includes(role)) {
      setExpandedRoles(expandedRoles.filter(r => r !== role));
    } else {
      setExpandedRoles([...expandedRoles, role]);
    }
  };

  // Toggle combination selection
  const toggleCombination = (role, cpeh) => {
    const key = `${role}|${cpeh}`;
    const exists = selectedCombinations.some(c => `${c.role}|${c.cpeh}` === key);
    
    if (exists) {
      setSelectedCombinations(selectedCombinations.filter(c => `${c.role}|${c.cpeh}` !== key));
    } else {
      setSelectedCombinations([...selectedCombinations, { role, cpeh }]);
    }
  };

  // Remove a specific combination
  const removeCombination = (role, cpeh) => {
    const key = `${role}|${cpeh}`;
    setSelectedCombinations(selectedCombinations.filter(c => `${c.role}|${c.cpeh}` !== key));
  };

  // Select all CPEHs for a role
  const selectAllForRole = (role) => {
    const availableCPEHs = roleCPEHMapping[role] || [];
    const newCombinations = [...selectedCombinations];
    
    availableCPEHs.forEach(cpeh => {
      const key = `${role}|${cpeh}`;
      const exists = newCombinations.some(c => `${c.role}|${c.cpeh}` === key);
      if (!exists) {
        newCombinations.push({ role, cpeh });
      }
    });
    
    setSelectedCombinations(newCombinations);
  };

  // Deselect all CPEHs for a role
  const deselectAllForRole = (role) => {
    setSelectedCombinations(selectedCombinations.filter(c => c.role !== role));
  };

  // Get selected CPEHs for a role
  const getSelectedCPEHsForRole = (role) => {
    return selectedCombinations
      .filter(c => c.role === role)
      .map(c => c.cpeh);
  };

  // Check if a combination is selected
  const isCombinationSelected = (role, cpeh) => {
    const key = `${role}|${cpeh}`;
    return selectedCombinations.some(c => `${c.role}|${c.cpeh}` === key);
  };

  // Get count of selected combinations for a role
  const getRoleSelectionCount = (role) => {
    return selectedCombinations.filter(c => c.role === role).length;
  };

  // Helper function to fetch with retry
  const fetchWithRetry = async (url, options, maxRetries = 3) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);
        return response;
      } catch (err) {
        if (attempt === maxRetries) throw err;
        // Wait longer between retries (2s, 4s, 6s)
        await new Promise(resolve => setTimeout(resolve, attempt * 2000));
      }
    }
  };

  // Helper to poll S3 with retries
  const pollS3Results = async (role, cpeh, startedAt, maxAttempts = 3) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // Wait 70 seconds before each attempt
      await new Promise(resolve => setTimeout(resolve, 70000));
      
      try {
        const s3Response = await fetchWithRetry(`${API_BASE}/security-field-test-last`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role_name: role, test_cpeh: cpeh })
        });
        
        if (s3Response.ok) {
          const data = await s3Response.json();
          if (isFreshResult(data, startedAt)) {
            return data;
          }
        }
        
        if (attempt < maxAttempts) {
          console.log(`S3 fetch attempt ${attempt} failed for ${role} ${cpeh}, retrying...`);
        }
      } catch (err) {
        console.error(`S3 fetch error on attempt ${attempt}:`, err);
      }
    }
    throw new Error(`Failed to fetch results from S3 after ${maxAttempts} attempts (waited ${maxAttempts * 70}s)`);
  };

  // Run tests for selected combinations in parallel
  const runSelectedTests = async () => {
    if (selectedCombinations.length === 0) {
      alert("Please select at least one role-CPEH combination");
      return;
    }

    setMultiTestRunning(true);
    setMultiTestResults([]);
    setMultiTestProgress({ completed: 0, total: selectedCombinations.length });
    setError(null);

    console.log(`Running ${selectedCombinations.length} tests in parallel...`);

    // Helper function to run a single test
    const runSingleTest = async (combo) => {
      const startedAt = Date.now();
      try {
        let response = null;
        let requestError = null;
        try {
          response = await fetch(`${API_BASE}/security-field-test`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              role_name: combo.role,
              test_cpeh: combo.cpeh,
              test_type: testType,
            }),
          });
        } catch (err) {
          requestError = err;
        }

        if (isBackgroundRun({ status: response?.status, error: requestError })) {
          // Test is running in background, poll for results
          console.log(`Test running in background for ${combo.role} - ${combo.cpeh}, polling S3...`);
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5s
          
          // Poll S3 for results
          const s3Result = await pollS3Results(combo.role, combo.cpeh, startedAt);
          if (s3Result) {
            return { role: combo.role, cpeh: combo.cpeh, data: s3Result };
          } else {
            return { 
              role: combo.role, 
              cpeh: combo.cpeh, 
              error: "Test timed out - results not found in S3" 
            };
          }
        } else if (requestError) {
          throw requestError;
        } else if (response.ok) {
          const data = await response.json();
          return { role: combo.role, cpeh: combo.cpeh, data };
        } else {
          const errorText = await response.text();
          return { role: combo.role, cpeh: combo.cpeh, error: errorText };
        }
      } catch (err) {
        console.error(`Error testing ${combo.role} - ${combo.cpeh}:`, err);
        return { role: combo.role, cpeh: combo.cpeh, error: err.message };
      }
    };

    // Run all tests in parallel
    const testPromises = selectedCombinations.map(combo => 
      runSingleTest(combo).then(result => {
        // Update progress as each test completes
        setMultiTestProgress(prev => ({
          completed: prev.completed + 1,
          total: prev.total
        }));
        return result;
      })
    );

    const results = await Promise.all(testPromises);
    setMultiTestResults(results);
    setMultiTestRunning(false);
  };

  // Fetch results from S3
  const fetchResultsFromS3 = async (startedAt) => {
    try {
      const response = await fetch(`${API_BASE}/security-field-test-last`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          role_name: selectedRole,
          test_cpeh: selectedCPEH
        })
      });

      if (response.ok) {
        const data = await response.json();
        return isFreshResult(data, startedAt) ? data : null;
      }
      return null;
    } catch (err) {
      console.error("Error fetching from S3:", err);
      return null;
    }
  };

  // Poll S3 for results
  const pollForResults = async (startTime) => {
    const maxPolls = 30; // Poll for up to 5 minutes (the test Lambda's timeout)
    let pollCount = 0;

    const poll = async () => {
      pollCount++;
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      setError(`⏳ Test running in background... (${elapsed}s elapsed)`);

      const results = await fetchResultsFromS3(startTime);
      
      if (results) {
        setTestResults(results);
        setLoading(false);
        setError(null);
        return;
      }

      if (pollCount < maxPolls) {
        setTimeout(poll, 10000); // Poll every 10 seconds
      } else {
        setError("Test timed out. Please try again or check S3 manually.");
        setLoading(false);
      }
    };

    poll();
  };

  // Field test execution
  const runFieldTest = async () => {
    setLoading(true);
    setError(null);
    setTestResults(null);
    const startTime = Date.now();

    try {
      // Determine test employees based on test type
      const testEmployees = testType === "quick" 
        ? ["21170110"]
        : ["21170110", "26452", "21121513", "20072684"];

      const response = await fetch(`${API_BASE}/security-field-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          role_name: selectedRole,
          test_cpeh: selectedCPEH,
          test_employees: testEmployees
        })
      });

      if (!response.ok) {
        if (isBackgroundRun({ status: response.status })) {
          // Start polling for results instead of showing error
          setError("⏳ Test running in background... (30s elapsed)");
          pollForResults(startTime);
          return;
        }
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setTestResults(data);
      setLoading(false);
    } catch (err) {
      // Check if fetch itself failed (network error)
      if (isBackgroundRun({ error: err })) {
        setError("⏳ Test running in background... (30s elapsed)");
        pollForResults(startTime);
      } else {
        setError(err.message);
        setLoading(false);
      }
    }
  };

  // Retrieve security classes
  const retrieveSecurityClasses = async () => {
    setLoading(true);
    setError(null);
    setSecurityClasses(null);

    try {
      const response = await fetch(`${API_BASE}/retrieve-security-classes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role_name: "GHR:H:BHRwithApprovals",
          data_area: "KOCHIND_AX5_HCM",
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setSecurityClasses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Run all 48 tests in parallel batches
  const runAllTests = async () => {
    setBulkTestRunning(true);
    setBulkTestResults([]);
    setError(null);
    
    // Generate all test combinations
    const allTests = [];
    Object.entries(roleCPEHMapping).forEach(([role, cpehList]) => {
      cpehList.forEach(cpeh => {
        allTests.push({ role, cpeh });
      });
    });
    
    setBulkTestProgress({ completed: 0, total: allTests.length });
    
    // Run tests in batches of 5 to avoid overwhelming the API
    const batchSize = 5;
    const results = [];
    
    for (let i = 0; i < allTests.length; i += batchSize) {
      const batch = allTests.slice(i, i + batchSize);
      const batchPromises = batch.map(async ({ role, cpeh }) => {
        try {
          // Trigger the test once; retrying would start a duplicate myHR test run.
          const startedAt = Date.now();
          let response = null;
          let requestError = null;
          try {
            response = await fetch(`${API_BASE}/security-field-test`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                role_name: role,
                test_cpeh: cpeh,
                test_employees: ["21170110", "26452", "21121513", "20072684"]
              })
            });
          } catch (err) {
            requestError = err;
          }
          
          let data;
          if (isBackgroundRun({ status: response?.status, error: requestError })) {
            data = await pollS3Results(role, cpeh, startedAt);
          } else if (requestError) {
            throw requestError;
          } else if (!response.ok) {
            throw new Error(`API error: ${response.status} ${(await response.text()).slice(0, 200)}`);
          } else {
            data = await response.json();
          }
          
          return {
            role,
            cpeh,
            status: data.validation?.overall_status || data.summary?.overall_status || "UNKNOWN",
            violations: data.validation?.violations || [],
            passed: data.validation?.passed || [],
            timestamp: new Date().toISOString()
          };
        } catch (err) {
          return {
            role,
            cpeh,
            status: "ERROR",
            error: err.message || "Unknown error occurred",
            violations: [],
            passed: [],
            timestamp: new Date().toISOString()
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      setBulkTestProgress({ completed: results.length, total: allTests.length });
      setBulkTestResults([...results]); // Update UI with incremental results
      
      // Save progress to localStorage
      localStorage.setItem('bulkTestResults', JSON.stringify(results));
      localStorage.setItem('bulkTestProgress', JSON.stringify({ completed: results.length, total: allTests.length }));
    }
    
    setBulkTestRunning(false);
  };
  
  // Export bulk results to CSV
  const exportBulkResults = () => {
    const csvRows = [];
    csvRows.push(["Role", "CPEH", "Status", "Violation Count", "Violation Details", "Error"]);
    
    bulkTestResults.forEach(result => {
      let violationDetails = "";
      
      if (result.violations && result.violations.length > 0) {
        violationDetails = result.violations
          .map(v => {
            // Handle different violation formats
            if (typeof v === 'string') {
              return v;
            } else if (v && v.employee_id && v.violation) {
              return `Employee ${v.employee_id}: ${v.violation}`;
            } else if (v && v.violation) {
              return v.violation;
            } else {
              return JSON.stringify(v);
            }
          })
          .filter(v => v && v !== 'undefined')
          .join("; ");
      }
      
      csvRows.push([
        result.role,
        result.cpeh,
        result.status,
        result.violations ? result.violations.length : 0,
        violationDetails || "No violations",
        result.error || ""
      ]);
    });
    
    const csvContent = csvRows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-field-test-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  // Load previous results from localStorage
  const loadPreviousResults = () => {
    const savedResults = localStorage.getItem('bulkTestResults');
    const savedProgress = localStorage.getItem('bulkTestProgress');
    
    if (savedResults) {
      const results = JSON.parse(savedResults);
      setBulkTestResults(results);
      
      if (savedProgress) {
        setBulkTestProgress(JSON.parse(savedProgress));
      }
      
      setError(`Loaded ${results.length} previous test results`);
      setTimeout(() => setError(null), 3000);
    } else {
      setError("No previous results found");
      setTimeout(() => setError(null), 3000);
    }
  };
  
  // Clear all results
  const clearBulkResults = () => {
    if (window.confirm("Clear all test results? This cannot be undone.")) {
      setBulkTestResults([]);
      setBulkTestProgress({ completed: 0, total: 0 });
      localStorage.removeItem('bulkTestResults');
      localStorage.removeItem('bulkTestProgress');
      setError("Results cleared");
      setTimeout(() => setError(null), 2000);
    }
  };

  return (
    <div className="cug-root sft-root">
      {/* Header matching other tabs */}
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark">V</div>
          <div>
            <div className="cug-title">Security Field Testing</div>
            <div className="cug-subtitle">Automated validation of compensation and PII field access for myHR roles</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sft-tabs">
        <button
          className={`sft-tab ${activeTab === "field-test" ? "sft-tab-active" : ""}`}
          onClick={() => setActiveTab("field-test")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          Single Test
        </button>
        <button
          className={`sft-tab ${activeTab === "bulk-test" ? "sft-tab-active" : ""}`}
          onClick={() => setActiveTab("bulk-test")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 7h16M4 12h16M4 17h16"/>
          </svg>
          Run All Tests (48)
        </button>
        <button
          className={`sft-tab ${activeTab === "security-classes" ? "sft-tab-active" : ""}`}
          onClick={() => setActiveTab("security-classes")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Security Classes
        </button>
      </div>

      {/* Field Test Tab */}
      {activeTab === "field-test" && (
        <div className="sft-content">
          {/* Multi-Select Toggle */}
          <div className="sft-multi-select-toggle">
            <button
              className={`sft-toggle-btn ${multiSelectMode ? 'active' : ''}`}
              onClick={toggleMultiSelectMode}
            >
              {multiSelectMode ? '✓ Multi-Select Mode' : 'Enable Multi-Select'}
            </button>
            {multiSelectMode && (
              <span className="sft-selected-count">
                {selectedCombinations.length} scenario{selectedCombinations.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          {!multiSelectMode && (
          <div className="sft-inline-form">
            <div className="sft-form-row">
              <div className="sft-form-field">
                <label className="sft-label">Role</label>
                <select
                  className="sft-select sft-select-inline"
                  value={selectedRole}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  {availableRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div className="sft-form-field">
                <label className="sft-label">CPEH Pattern</label>
                <select
                  className="sft-select sft-select-inline"
                  value={selectedCPEH}
                  onChange={(e) => setSelectedCPEH(e.target.value)}
                >
                  {getAvailableCPEH(selectedRole).map(cpeh => (
                    <option key={cpeh.value} value={cpeh.value}>{cpeh.label}</option>
                  ))}
                </select>
              </div>

              <div className="sft-form-field">
                <label className="sft-label">Scope</label>
                <select
                  className="sft-select sft-select-inline"
                  value={testType}
                  onChange={(e) => setTestType(e.target.value)}
                >
                  <option value="full_matrix">Full Matrix (4 employees)</option>
                  <option value="quick">Quick (1 employee)</option>
                </select>
              </div>

              <button
                className="sft-run-btn-inline"
                onClick={runFieldTest}
                disabled={loading}
              >
                {loading ? "Running..." : "Run Test"}
              </button>
            </div>
          </div>
          )}

          {/* Multi-Select Checkbox List */}
          {multiSelectMode && (
            <div className="sft-multi-select-container">
              {/* Selected Combinations Display */}
              {selectedCombinations.length > 0 && (
                <div className="sft-selected-combinations">
                  <div className="sft-selected-header">
                    <h4>Selected Combinations ({selectedCombinations.length})</h4>
                    <button
                      className="sft-clear-all-btn"
                      onClick={() => setSelectedCombinations([])}
                      title="Clear all selections"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="sft-selected-chips">
                    {selectedCombinations.map((combo, idx) => (
                      <div key={idx} className="sft-selection-chip">
                        <span className="sft-chip-role">{combo.role.replace('GHR:H:', '')}</span>
                        <span className="sft-chip-separator">·</span>
                        <span className="sft-chip-cpeh">{combo.cpeh}</span>
                        <button
                          className="sft-chip-remove"
                          onClick={() => removeCombination(combo.role, combo.cpeh)}
                          title="Remove this combination"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="sft-role-selector">
                <div className="sft-selector-header">
                  <h4>Select Roles & CPEH Patterns</h4>
                  <div className="sft-selector-actions">
                    <select
                      className="sft-select-scope"
                      value={testType}
                      onChange={(e) => setTestType(e.target.value)}
                    >
                      <option value="full_matrix">Full Matrix (4 employees)</option>
                      <option value="quick">Quick (1 employee)</option>
                    </select>
                    <button
                      className="sft-run-btn-inline"
                      onClick={runSelectedTests}
                      disabled={multiTestRunning || selectedCombinations.length === 0}
                    >
                      {multiTestRunning ? 'Running...' : `Run ${selectedCombinations.length} Test${selectedCombinations.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>

                <div className="sft-role-list">
                  {availableRoles.map(role => {
                    const isExpanded = expandedRoles.includes(role);
                    const selectionCount = getRoleSelectionCount(role);
                    const availableCPEHs = roleCPEHMapping[role] || [];
                    const selectedCPEHs = getSelectedCPEHsForRole(role);
                    const allSelected = availableCPEHs.length > 0 && selectedCPEHs.length === availableCPEHs.length;
                    
                    return (
                      <div key={role} className={`sft-role-item ${selectionCount > 0 ? 'sft-role-has-selections' : ''}`}>
                        <div 
                          className="sft-role-header"
                          onClick={() => toggleRoleExpansion(role)}
                        >
                          <div className="sft-role-info">
                            <span className="sft-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                            <span className="sft-role-name">{role.replace('GHR:H:', '')}</span>
                            {selectionCount > 0 && (
                              <div className="sft-selected-cpehs">
                                {selectedCPEHs.slice(0, 3).map(cpeh => (
                                  <span key={cpeh} className="sft-cpeh-mini-badge">{cpeh}</span>
                                ))}
                                {selectedCPEHs.length > 3 && (
                                  <span className="sft-cpeh-mini-badge">+{selectedCPEHs.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="sft-cpeh-list">
                            <div className="sft-cpeh-actions">
                              <button
                                className="sft-cpeh-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  allSelected ? deselectAllForRole(role) : selectAllForRole(role);
                                }}
                              >
                                {allSelected ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                            {availableCPEHs.map(cpeh => (
                              <label key={cpeh} className="sft-cpeh-option">
                                <input
                                  type="checkbox"
                                  checked={isCombinationSelected(role, cpeh)}
                                  onChange={() => toggleCombination(role, cpeh)}
                                />
                                <span className="sft-cpeh-label">{cpehLabels[cpeh]}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {multiTestRunning && (
                <div className="sft-progress" style={{ marginTop: '16px' }}>
                  <div className="sft-progress-bar">
                    <div
                      className="sft-progress-fill"
                      style={{ width: `${(multiTestProgress.completed / multiTestProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="sft-progress-text">
                    Completed {multiTestProgress.completed} / {multiTestProgress.total} tests
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="sft-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          {!multiSelectMode && testResults && (
            <TestResultsDisplay results={testResults} role={selectedRole} cpeh={selectedCPEH} />
          )}

          {/* Multi-Test Results */}
          {multiSelectMode && multiTestResults.length > 0 && (
            <div className="sft-multi-results">
              {multiTestResults.map((result, idx) => (
                <MultiTestResultRow 
                  key={idx} 
                  result={result} 
                  cpehLabel={cpehLabels[result.cpeh]}
                />
              ))}
            </div>
          )}

          <div className="sft-info-card">
            <details className="sft-info-details">
              <summary className="sft-info-summary">Test Configuration Details</summary>
              <div className="sft-info-section">
                <h4 className="sft-info-subtitle">Test Employees</h4>
                <ul className="sft-info-list">
                  <li><strong>21170110</strong> - Standard (no restrictions)</li>
                  <li><strong>26452</strong> - Executive Restrict</li>
                <li><strong>21121513</strong> - HR Restrict</li>
                <li><strong>20072684</strong> - Both Restrict (E+H)</li>
              </ul>
            </div>
            <div className="sft-info-section">
              <p className="sft-info-text"><strong>Comp:</strong> BaseRate, BaseSalary, AnnualSalary, etc.</p>
              <p className="sft-info-text"><strong>PII:</strong> SSN, DateOfBirth, HomeAddress, etc.</p>
            </div>
          </details>
        </div>
      </div>
    )}

      {/* Bulk Test Tab */}
      {activeTab === "bulk-test" && (
        <div className="sft-content">
          <div className="sft-inline-form">
            <div className="sft-form-row">
              <div className="sft-info-text" style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: "14px", color: "var(--text-secondary)" }}>
                  Execute all 48 role-CPEH combinations. Tests run in parallel and may take 60-90 minutes.
                </p>
              </div>

              <button
                className="sft-run-btn-inline"
                onClick={runAllTests}
                disabled={bulkTestRunning}
              >
                {bulkTestRunning ? "Running Tests..." : "▶ Run All 48 Tests"}
              </button>
              
              <button
                className="sft-run-btn-inline"
                onClick={loadPreviousResults}
                disabled={bulkTestRunning}
                style={{ background: "#8b5cf6" }}
                title="Load test results from interrupted session"
              >
                📂 Load Previous
              </button>

              {bulkTestResults.length > 0 && (
                <>
                  <button
                    className="sft-run-btn-inline"
                    onClick={exportBulkResults}
                    style={{ background: "#10b981" }}
                  >
                    📥 Export CSV
                  </button>

                  <select
                    className="sft-select sft-select-inline"
                    value={bulkTestFilter}
                    onChange={(e) => setBulkTestFilter(e.target.value)}
                    style={{ minWidth: "180px" }}
                  >
                    <option value="all">All ({bulkTestResults.length})</option>
                    <option value="failed">Failed ({bulkTestResults.filter(r => r.status === "FAIL").length})</option>
                    <option value="passed">Passed ({bulkTestResults.filter(r => r.status === "PASS").length})</option>
                  </select>
                  
                  <button
                    className="sft-run-btn-inline"
                    onClick={clearBulkResults}
                    disabled={bulkTestRunning}
                    style={{ background: "#ef4444" }}
                    title="Clear all test results"
                  >
                    🗑️ Clear
                  </button>
                </>
              )}
            </div>
          </div>

          {bulkTestRunning && (
            <div className="sft-progress">
              <div className="sft-progress-bar">
                <div
                  className="sft-progress-fill"
                  style={{ width: `${(bulkTestProgress.completed / bulkTestProgress.total) * 100}%` }}
                />
              </div>
              <p className="sft-progress-text">
                Testing {bulkTestProgress.completed} / {bulkTestProgress.total} scenarios...
              </p>
            </div>
          )}

          {bulkTestResults.length > 0 && (
            <BulkTestResultsTable results={bulkTestResults} filter={bulkTestFilter} />
          )}
        </div>
      )}

      {/* Security Classes Tab */}
      {activeTab === "security-classes" && (
        <div className="sft-content">
          <div className="sft-card">
            <h2 className="sft-card-title">Security Class Analysis</h2>
            <p className="sft-card-description">
              Retrieves and analyzes all security classes assigned to GHR:H:BHRwithApprovals role,
              identifying which classes control compensation and PII field access.
            </p>

            <button
              className="compare-btn sft-run-btn"
              onClick={retrieveSecurityClasses}
              disabled={loading}
            >
              {loading ? "Retrieving..." : "Retrieve Security Classes"}
            </button>

            {error && (
              <div className="sft-error">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            )}

            {securityClasses && (
              <div className="sft-results">
                <SecurityClassesDisplay data={securityClasses} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Component for collapsible multi-test result row
function MultiTestResultRow({ result, cpehLabel }) {
  const [expanded, setExpanded] = useState(false);
  
  const roleName = result.role.replace('GHR:H:', '');
  const status = result.error 
    ? 'ERROR' 
    : (result.data?.validation?.overall_status || result.data?.summary?.overall_status || 'UNKNOWN');
  
  return (
    <div className="sft-multi-result-row">
      <div 
        className="sft-multi-result-row-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="sft-multi-result-info">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`sft-multi-expand-icon ${expanded ? "sft-multi-expand-icon-rotated" : ""}`}
          >
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span className="sft-multi-result-title">{roleName} - {cpehLabel}</span>
        </div>
        <div className="sft-multi-result-status">
          {status === 'PASS' && (
            <span className="sft-status-badge sft-status-badge-pass">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              PASS
            </span>
          )}
          {status === 'FAIL' && (
            <span className="sft-status-badge sft-status-badge-fail">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              FAIL
            </span>
          )}
          {status === 'ERROR' && (
            <span className="sft-status-badge sft-status-badge-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              ERROR
            </span>
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="sft-multi-result-content">
          {result.data ? (
            <TestResultsDisplay results={result.data} role={result.role} cpeh={result.cpeh} showHeader={false} />
          ) : (
            <div className="sft-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {result.error || 'Unknown error'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Component to display test results
function TestResultsDisplay({ results, role, cpeh, showHeader = true }) {
  const summary = results.summary || {};
  const validation = results.validation || {};
  const testResults = results.test_results || [];
  
  const overallStatus = summary.overall_status || validation.overall_status || "UNKNOWN";
  const compFields = summary.compensation_fields_tested || [];
  const piiFields = summary.pii_fields_tested || [];
  const violations = validation.violations || [];
  const passed = validation.passed || [];

  return (
    <div className="sft-test-results">
      {/* Status Banner */}
      <div className={`sft-status-banner ${overallStatus === "PASS" ? "sft-status-pass" : "sft-status-fail"}`}>
        <div className="sft-status-icon">
          {overallStatus === "PASS" ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          ) : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          )}
        </div>
        <div className="sft-status-content">
          <h3 className="sft-status-title">
            {overallStatus === "PASS" ? "✅ All Tests Passed" : "❌ Security Violations Detected"}
          </h3>
          <p className="sft-status-text">
            Tested {summary.employees_tested || 0} employees with {compFields.length} compensation fields and {piiFields.length} PII fields
          </p>
          <p className="sft-status-text">
            {validation.passed_count || 0} checks passed, {validation.violations_count || 0} violations found
          </p>
          {results.cache_used && (
            <p className="sft-cache-indicator">
              ⚡ Using cached security class definitions (refreshes every 12 hours)
            </p>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="sft-summary-grid">
        <div className="sft-summary-card sft-card-comp">
          <div className="sft-summary-value">{compFields.length}</div>
          <div className="sft-summary-label">Compensation Fields Tested</div>
        </div>
        <div className="sft-summary-card sft-card-pii">
          <div className="sft-summary-value">{piiFields.length}</div>
          <div className="sft-summary-label">PII Fields Tested</div>
        </div>
        <div className="sft-summary-card sft-card-employees">
          <div className="sft-summary-value">{summary.employees_tested || 0}</div>
          <div className="sft-summary-label">Employees Tested</div>
        </div>
        <div className="sft-summary-card sft-card-classes">
          <div className="sft-summary-value">{summary.security_classes_found || 0}</div>
          <div className="sft-summary-label">Security Classes</div>
        </div>
      </div>

      {/* Violations Section */}
      {violations.length > 0 && (
        <div className="sft-violations">
          <h4 className="sft-violations-title">🚨 Security Violations - Fields Leaking Data</h4>
          <div className="sft-violations-list">
            {violations.map((violation, idx) => (
              <div key={idx} className="sft-violation-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {violation}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Passed Tests Section */}
      {passed.length > 0 && (
        <div className="sft-passed-tests">
          <h4 className="sft-passed-title">✅ Passed Validations</h4>
          <div className="sft-passed-list">
            {passed.map((pass, idx) => (
              <div key={idx} className="sft-passed-item">{pass}</div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Employee Results - All Employees */}
      {testResults.length > 0 && (
        <div className="sft-employee-results">
          <h4 className="sft-section-title">Detailed Employee Test Results</h4>
          <p className="sft-section-description">Expand any employee to see exactly which fields were tested and their results</p>
          {testResults.map((emp, idx) => (
            <DetailedEmployeeCard key={idx} employee={emp} expectedAccess={validation.expected_access} />
          ))}
        </div>
      )}

      {/* Fields Tested Accordion */}
      <div className="sft-fields-tested">
        <FieldsAccordion title="Compensation Fields Tested" fields={compFields} type="compensation" />
        <FieldsAccordion title="PII Fields Tested" fields={piiFields} type="pii" />
      </div>
    </div>
  );
}

// Component to display detailed employee results
function DetailedEmployeeCard({ employee, expectedAccess }) {
  const [compExpanded, setCompExpanded] = useState(false);
  const [piiExpanded, setPiiExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const compTest = employee.compensation_test || {};
  const piiTest = employee.pii_test || {};
  
  // Determine if fields are violations based on expected access
  const hasCompAccess = expectedAccess?.compensation || false;
  const hasPiiAccess = expectedAccess?.pii || false;
  
  // Determine population restrictions
  const hasExecAccess = expectedAccess?.executive_population || false;
  const hasHrAccess = expectedAccess?.hr_population || false;
  const restriction = employee.restriction_type || 'standard';
  
  let shouldSeeEmployee = true;
  if (restriction === 'exec_restrict' && !hasExecAccess) {
    shouldSeeEmployee = false;
  } else if (restriction === 'hr_restrict' && !hasHrAccess) {
    shouldSeeEmployee = false;
  } else if (restriction === 'both_restrict' && !(hasExecAccess && hasHrAccess)) {
    shouldSeeEmployee = false;
  }
  
  // Determine violations
  const compIsViolation = shouldSeeEmployee && (
    (!hasCompAccess && (compTest.visible_fields?.length || 0) > 0) ||
    (!shouldSeeEmployee && (compTest.visible_fields?.length || 0) > 0)
  );
  
  const piiIsViolation = shouldSeeEmployee && (
    (!hasPiiAccess && (piiTest.visible_fields?.length || 0) > 0) ||
    (!shouldSeeEmployee && (piiTest.visible_fields?.length || 0) > 0)
  );

  // Filter fields based on search term
  const filterFields = (fields) => {
    if (!searchTerm.trim()) return fields;
    const search = searchTerm.toLowerCase();
    return fields.filter(field => {
      const fieldName = typeof field === 'string' ? field : field.field_name;
      const businessClass = typeof field === 'string' ? '' : field.business_class;
      const securityClass = typeof field === 'string' ? '' : (field.security_class || '');
      return fieldName.toLowerCase().includes(search) || 
             businessClass.toLowerCase().includes(search) ||
             securityClass.toLowerCase().includes(search);
    });
  };

  const filteredCompFields = compTest.visible_fields ? filterFields(compTest.visible_fields) : [];
  const filteredPiiFields = piiTest.visible_fields ? filterFields(piiTest.visible_fields) : [];
  
  // Get unique business classes for display
  const getUniqueBusinessClasses = (fields) => {
    if (!fields || fields.length === 0) return [];
    const classes = new Set();
    fields.forEach(field => {
      const bc = typeof field === 'string' ? null : field.business_class;
      if (bc) classes.add(bc);
    });
    return Array.from(classes);
  };
  
  const compBusinessClasses = getUniqueBusinessClasses(compTest.visible_fields || []);
  const piiBusinessClasses = getUniqueBusinessClasses(piiTest.visible_fields || []);
  
  return (
    <div className="sft-employee-card">
      <div className="sft-employee-header">
        <div className="sft-employee-info">
          <span className="sft-employee-id">Employee {employee.employee_id}</span>
          <span className="sft-employee-type">{employee.restriction_type || "standard"}</span>
        </div>
        <div className="sft-employee-stats">
          <button
            className={`sft-stat-badge sft-stat-comp ${compIsViolation ? 'sft-stat-violation' : ''}`}
            onClick={() => setCompExpanded(!compExpanded)}
            title="Click to toggle compensation fields"
          >
            {compTest.visible_fields?.length || 0} comp {(compTest.visible_fields?.length || 0) === 1 ? 'field' : 'fields'}
          </button>
          <button
            className={`sft-stat-badge sft-stat-pii ${piiIsViolation ? 'sft-stat-violation' : ''}`}
            onClick={() => setPiiExpanded(!piiExpanded)}
            title="Click to toggle PII fields"
          >
            {piiTest.visible_fields?.length || 0} PII {(piiTest.visible_fields?.length || 0) === 1 ? 'field' : 'fields'}
          </button>
        </div>
      </div>

      {(compExpanded || piiExpanded) && (
        <div className="sft-employee-details">
          <div className="sft-search-container">
            <input
              type="text"
              className="sft-search-input"
              placeholder="Search fields, business class, or security class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button 
                className="sft-search-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {compExpanded && (
          <div className="sft-field-group">
            <h5 className="sft-field-group-title">
              Compensation Fields Visible
              {compBusinessClasses.length > 0 && (
                <span className="sft-business-class-label">
                  Business {compBusinessClasses.length === 1 ? 'Class' : 'Classes'}: {compBusinessClasses.join(', ')}
                </span>
              )}
              {searchTerm && (
                <span className="sft-search-results-count">
                  ({filteredCompFields.length} of {compTest.visible_fields?.length || 0})
                </span>
              )}
            </h5>
            {filteredCompFields.length > 0 ? (
              <div className="sft-field-list">
                {filteredCompFields.map((field, i) => {
                  const fieldName = typeof field === 'string' ? field : field.field_name;
                  const businessClass = typeof field === 'string' ? (compTest.business_class || 'WorkAssignment') : field.business_class;
                  const securityClass = typeof field === 'string' ? null : field.security_class;
                  
                  return (
                    <div key={i} className={`sft-field-item sft-field-item-comp ${compIsViolation ? 'sft-field-violation' : ''}`}>
                      <span className="sft-field-bc">{businessClass}</span>
                      <span className="sft-field-dot">.</span>
                      <span className="sft-field-name">{fieldName}</span>
                      {securityClass && securityClass !== 'Unknown' && (
                        <span className="sft-security-class" title="Security Class">{securityClass}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="sft-no-fields">
                {searchTerm ? 'No matching compensation fields' : 'No compensation fields visible'}
              </p>
            )}  
          </div>
          )}
          
          {piiExpanded && (
          <div className="sft-field-group">
            <h5 className="sft-field-group-title">
              PII Fields Visible
              {piiBusinessClasses.length > 0 && (
                <span className="sft-business-class-label">
                  Business {piiBusinessClasses.length === 1 ? 'Class' : 'Classes'}: {piiBusinessClasses.join(', ')}
                </span>
              )}
              {searchTerm && (
                <span className="sft-search-results-count">
                  ({filteredPiiFields.length} of {piiTest.visible_fields?.length || 0})
                </span>
              )}
            </h5>
            {filteredPiiFields.length > 0 ? (
              <div className="sft-field-list">
                {filteredPiiFields.map((field, i) => {
                  const fieldName = typeof field === 'string' ? field : field.field_name;
                  const businessClass = typeof field === 'string' ? (piiTest.business_class || 'Employee') : field.business_class;
                  const securityClass = typeof field === 'string' ? null : field.security_class;
                  
                  return (
                    <div key={i} className={`sft-field-item sft-field-item-pii ${piiIsViolation ? 'sft-field-violation' : ''}`}>
                      <span className="sft-field-bc">{businessClass}</span>
                      <span className="sft-field-dot">.</span>
                      <span className="sft-field-name">{fieldName}</span>
                      {securityClass && securityClass !== 'Unknown' && (
                        <span className="sft-security-class" title="Security Class">{securityClass}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="sft-no-fields">
                {searchTerm ? 'No matching PII fields' : 'No PII fields visible'}
              </p>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// Component for fields accordion
function FieldsAccordion({ title, fields, type }) {
  const [expanded, setExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter fields based on search term
  const filteredFields = searchTerm.trim()
    ? fields.filter(field => field.toLowerCase().includes(searchTerm.toLowerCase()))
    : fields;

  return (
    <div className="sft-fields-accordion">
      <div className="sft-accordion-header" onClick={() => setExpanded(!expanded)}>
        <div className="sft-accordion-title-group">
          <h5 className="sft-accordion-title">
            {title} ({fields.length})
            {searchTerm && filteredFields.length !== fields.length && (
              <span className="sft-search-count"> - {filteredFields.length} matching</span>
            )}
          </h5>
          {expanded && (
            <div className="sft-accordion-search" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="sft-accordion-search-input"
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="sft-accordion-search-clear"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`sft-expand-icon ${expanded ? "sft-expand-icon-rotated" : ""}`}
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {expanded && (
        <div className="sft-accordion-content">
          {filteredFields.length > 0 ? (
            <div className="sft-field-tags">
              {filteredFields.map((field, i) => (
                <span key={i} className={`sft-field-tag sft-tag-${type}`}>{field}</span>
              ))}
            </div>
          ) : (
            <p className="sft-no-results">No fields match your search</p>
          )}
        </div>
      )}
    </div>
  );
}

// Component to display security classes
function SecurityClassesDisplay({ data }) {
  const summary = data.summary || {};
  const classes = data.security_classes || [];

  return (
    <div className="sft-security-classes">
      <div className="sft-summary-grid">
        <div className="sft-summary-card">
          <div className="sft-summary-value">{summary.total_classes || 0}</div>
          <div className="sft-summary-label">Total Security Classes</div>
        </div>
        <div className="sft-summary-card">
          <div className="sft-summary-value">{summary.compensation_related || 0}</div>
          <div className="sft-summary-label">Compensation Related</div>
        </div>
        <div className="sft-summary-card">
          <div className="sft-summary-value">{summary.pii_related || 0}</div>
          <div className="sft-summary-label">PII Related</div>
        </div>
      </div>

      {summary.s3_location && (
        <div className="sft-s3-info">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          <span>Full details stored in S3: <code>{summary.s3_location}</code></span>
        </div>
      )}

      {classes.length > 0 && (
        <div className="sft-classes-list">
          <h4 className="sft-section-title">Security Classes</h4>
          <div className="sft-classes-grid">
            {classes.map((cls, idx) => (
              <div key={idx} className={`sft-class-card ${cls.is_compensation_related ? "sft-class-comp" : ""}`}>
                <div className="sft-class-name">{cls.class_name}</div>
                {cls.is_compensation_related && (
                  <span className="sft-class-badge">Compensation</span>
                )}
                {cls.business_classes && cls.business_classes.length > 0 && (
                  <div className="sft-class-meta">
                    {cls.business_classes.length} business class{cls.business_classes.length !== 1 ? "es" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Component to display bulk test results in a table
function BulkTestResultsTable({ results, filter }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  const filteredResults = results.filter(result => {
    if (filter === "failed") return result.status === "FAIL";
    if (filter === "passed") return result.status === "PASS";
    return true;
  });
  
  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };
  
  return (
    <div className="sft-bulk-results">
      <table className="sft-bulk-table">
        <thead>
          <tr>
            <th style={{ width: "50px" }}></th>
            <th style={{ width: "80px" }}>Status</th>
            <th>Role</th>
            <th>CPEH</th>
            <th style={{ width: "120px" }}>Violations</th>
            <th style={{ width: "100px" }}>Details</th>
          </tr>
        </thead>
        <tbody>
          {filteredResults.map((result, index) => (
            <React.Fragment key={`${result.role}-${result.cpeh}`}>
              <tr className={`sft-bulk-row ${result.status === "FAIL" ? "sft-bulk-row-fail" : "sft-bulk-row-pass"}`}>
                <td style={{ textAlign: "center" }}>
                  {(result.violations.length > 0 || result.error) && (
                    <button
                      className="sft-expand-btn"
                      onClick={() => toggleRow(index)}
                      title={result.error ? "Show error details" : "Show violation details"}
                    >
                      {expandedRows.has(index) ? "▼" : "▶"}
                    </button>
                  )}
                </td>
                <td>
                  <span className={`sft-status-badge ${result.status === "PASS" ? "sft-status-badge-pass" : "sft-status-badge-fail"}`}>
                    {result.status === "PASS" ? "✓ PASS" : result.status === "ERROR" ? "⚠ ERROR" : "✗ FAIL"}
                  </span>
                </td>
                <td className="sft-bulk-cell-role">{result.role}</td>
                <td className="sft-bulk-cell-cpeh">{result.cpeh}</td>
                <td style={{ textAlign: "center" }}>
                  {result.violations.length > 0 ? (
                    <span className="sft-violation-count">{result.violations.length}</span>
                  ) : (
                    <span style={{ color: "#10b981" }}>0</span>
                  )}
                </td>
                <td>
                  {result.error && (
                    <span className="sft-error-text" title={result.error}>⚠ Error</span>
                  )}
                </td>
              </tr>
              {expandedRows.has(index) && result.error && (
                <tr className="sft-bulk-details-row">
                  <td colSpan="6">
                    <div className="sft-violations-expanded">
                      <h4 className="sft-violations-title" style={{ color: "var(--orange)" }}>Error Details:</h4>
                      <div className="sft-violation-item" style={{ borderLeftColor: "var(--orange)" }}>
                        <span className="sft-violation-icon" style={{ color: "var(--orange)" }}>⚠</span>
                        <div className="sft-violation-content">
                          <div className="sft-violation-text">{result.error}</div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {expandedRows.has(index) && result.violations.length > 0 && (
                <tr className="sft-bulk-details-row">
                  <td colSpan="6">
                    <div className="sft-violations-expanded">
                      <h4 className="sft-violations-title">Security Violations:</h4>
                      {result.violations.map((violation, vIdx) => {
                        // Handle different violation formats
                        let employeeId = "Unknown";
                        let employeeType = "";
                        let violationText = "";
                        
                        if (typeof violation === 'string') {
                          violationText = violation;
                        } else if (violation && typeof violation === 'object') {
                          employeeId = violation.employee_id || "Unknown";
                          employeeType = violation.employee_type || "";
                          violationText = violation.violation || JSON.stringify(violation);
                        }
                        
                        return (
                          <div key={vIdx} className="sft-violation-item">
                            <span className="sft-violation-icon">⚠</span>
                            <div className="sft-violation-content">
                              <div className="sft-violation-employee">
                                Employee {employeeId}
                                {employeeType && (
                                  <span className="sft-violation-type">({employeeType})</span>
                                )}
                              </div>
                              <div className="sft-violation-text">{violationText}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      
      {filteredResults.length === 0 && (
        <div className="sft-empty-state">
          <p>No results to display</p>
        </div>
      )}
    </div>
  );
}

export default SecurityFieldTesting;
